import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../api';
import Pagination from '../common/Pagination';
import Modal from '../Modal';
import ConfirmationModal from '../ConfirmationModal';
import {
  JuryHeader,
  JuryList,
  JuryForm,
  JuryDetails
} from './jurys';

const JurysTab = ({ onDataChange }) => {
  const [jurys, setJurys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(null);

  // États pour les modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedJury, setSelectedJury] = useState(null);
  const [detailJury, setDetailJury] = useState(null);
  
  // Données de référence
  const [typesSoutenance, setTypesSoutenance] = useState([]);
  const [enseignantsInternes, setEnseignantsInternes] = useState([]);
  const [enseignantsExternes, setEnseignantsExternes] = useState([]);

  const [formData, setFormData] = useState({
    nom: '',
    type_soutenance: '',
    president_interne: '',
    president_externe: '',
    membres: []
  });
  const [formErrors, setFormErrors] = useState({});

  // États pour la confirmation
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [juryToDelete, setJuryToDelete] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState({});

  const fetchJurys = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        page: currentPage,
        page_size: pageSize,
        ...(searchTerm && { search: searchTerm })
      };

      const response = await api.get('/soutenance/jurys/', { params });
      
      if (response.data.status === 'success') {
        setJurys(response.data.data);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Erreur:', error);
      setError('Erreur lors du chargement');
      toast.error('Erreur lors du chargement des jurys');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm]);

  const fetchReferenceData = useCallback(async () => {
    try {
      const [typesRes, ensIntRes, ensExtRes] = await Promise.all([
        api.get('/soutenance/types-soutenance/'),
        api.get('/enseignant/enseignants/'),
        api.get('/soutenance/enseignants-externes/')
      ]);
      
      if (typesRes.data.status === 'success') {
        setTypesSoutenance(typesRes.data.data || []);
      }
      
      if (ensIntRes.data.status === 'success') {
        setEnseignantsInternes(ensIntRes.data.data || []);
      }
      
      if (ensExtRes.data.status === 'success') {
        setEnseignantsExternes(ensExtRes.data.data || []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données de référence:', error);
      toast.error('Erreur lors du chargement des données de référence');
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentPage === 1) {
        fetchJurys();
      } else {
        setCurrentPage(1);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    fetchJurys();
  }, [fetchJurys]);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleOpenModal = (jury = null) => {
    if (jury) {
      setSelectedJury(jury);
    setFormData({
      nom: jury.nom,
      type_soutenance: jury.type_soutenance?.id || '',
      president_interne: jury.president_interne?.id || '',
      president_externe: jury.president_externe?.id || '',
      membres: jury.membres_jury?.map(m => ({
        enseignant_interne: m.enseignant_interne?.id || '',
        enseignant_externe: m.enseignant_externe?.id || '',
        role: m.role
      })) || []
    });
    } else {
      setSelectedJury(null);
    setFormData({
      nom: '',
      type_soutenance: '',
      president_interne: '',
      president_externe: '',
      membres: []
    });
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.nom.trim()) {
      errors.nom = "Le nom du jury est requis";
    }
    
    if (!formData.type_soutenance) {
      errors.type_soutenance = "Le type de soutenance est requis";
    }

    // Vérifier qu'il y a exactement un président
    const hasPresidentInterne = formData.president_interne;
    const hasPresidentExterne = formData.president_externe;
    
    if (!hasPresidentInterne && !hasPresidentExterne) {
      errors.president = "Un président (interne ou externe) est requis";
    } else if (hasPresidentInterne && hasPresidentExterne) {
      errors.president = "Il ne peut y avoir qu'un seul président";
    }

    // Valider les membres du jury
    if (formData.membres && formData.membres.length > 0) {
      formData.membres.forEach((membre, index) => {
        const hasInterneMembre = membre.enseignant_interne;
        const hasExterneMembre = membre.enseignant_externe;
        
        if (!hasInterneMembre && !hasExterneMembre) {
          errors[`membre_${index}`] = "Un enseignant doit être sélectionné";
        } else if (hasInterneMembre && hasExterneMembre) {
          errors[`membre_${index}`] = "Un seul enseignant par membre";
        }
      });
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      
      const dataToSend = {
        nom: formData.nom,
        type_soutenance: formData.type_soutenance,
        president_interne: formData.president_interne || null,
        president_externe: formData.president_externe || null,
        membres: formData.membres.map(membre => ({
          enseignant_interne: membre.enseignant_interne || null,
          enseignant_externe: membre.enseignant_externe || null,
          role: membre.role
        }))
      };

      if (selectedJury) {
        await api.put(`/soutenance/jurys/${selectedJury.id}/`, dataToSend);
        toast.success('Jury modifié avec succès');
      } else {
        await api.post('/soutenance/jurys/', dataToSend);
        toast.success('Jury créé avec succès');
      }

      setIsModalOpen(false);
      await fetchJurys();
      onDataChange && onDataChange();
    } catch (error) {
      console.error('Erreur:', error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      const errorMessage = error.response?.data?.message || 'Erreur lors de la sauvegarde';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewDetails = (jury) => {
    setDetailJury(jury);
    setIsDetailModalOpen(true);
  };

  const handlePrepareDelete = (jury) => {
    setJuryToDelete(jury);
    setConfirmConfig({
      title: 'Supprimer le jury',
      message: `Êtes-vous sûr de vouloir supprimer le jury "${jury.nom}" ?`,
      confirmText: 'Supprimer',
      type: 'danger'
    });
    setIsConfirmModalOpen(true);
  };

  const handleDelete = async () => {
    if (!juryToDelete) return;

    try {
      setDeleteLoading(true);
      await api.delete(`/soutenance/jurys/${juryToDelete.id}/`);
      toast.success('Jury supprimé avec succès');
      
      setIsConfirmModalOpen(false);
      setJuryToDelete(null);
      await fetchJurys();
      onDataChange && onDataChange();
    } catch (error) {
      console.error('Erreur:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de la suppression';
      toast.error(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const renderModalFooter = () => (
    <div className="flex justify-end space-x-3">
                  <button
                    type="button"
        onClick={() => setIsModalOpen(false)}
        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        disabled={submitting}
                >
                  Annuler
                </button>
                <button
                  type="submit"
        form="jury-form"
        disabled={submitting}
        className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
        {submitting && (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
        )}
        <span>{selectedJury ? 'Modifier' : 'Créer'}</span>
                </button>
              </div>
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <JuryHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onAddNew={() => handleOpenModal()}
        />

        {/* Message d'erreur */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Contenu principal */}
        <div className="mt-6">
          <JuryList
            jurys={jurys}
            loading={loading}
            searchTerm={searchTerm}
            onEdit={handleOpenModal}
            onDelete={handlePrepareDelete}
            onViewDetails={handleViewDetails}
            onAddNew={() => handleOpenModal()}
          />
        </div>

        {/* Pagination */}
        {pagination && jurys.length > 0 && (
          <div className="mt-6">
            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
        </div>
      )}

        {/* Modal principal */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={selectedJury ? 'Modifier le Jury' : 'Nouveau Jury'}
          footer={renderModalFooter()}
          maxWidth="max-w-5xl"
        >
          <JuryForm
            formData={formData}
            formErrors={formErrors}
            selectedJury={selectedJury}
            onInputChange={handleInputChange}
            onSubmit={handleSubmit}
            submitting={submitting}
            typesSoutenance={typesSoutenance}
            enseignantsInternes={enseignantsInternes}
            enseignantsExternes={enseignantsExternes}
          />
        </Modal>

        {/* Modal de détails */}
        <Modal
          isOpen={isDetailModalOpen}
          onClose={() => setIsDetailModalOpen(false)}
          title="Détails du jury"
          maxWidth="max-w-4xl"
        >
          <JuryDetails jury={detailJury} />
        </Modal>

        {/* Modal de confirmation */}
        <ConfirmationModal
          isOpen={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleDelete}
          title={confirmConfig.title}
          message={confirmConfig.message}
          confirmText={confirmConfig.confirmText}
          cancelText="Annuler"
          type={confirmConfig.type}
          isLoading={deleteLoading}
        />
      </div>
    </div>
  );
};

export default JurysTab;
